import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faEdit,
    faTrash,
    faTimes
} from "@fortawesome/free-solid-svg-icons";

import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../components/Ui_Element/DotLoader";
import { post, getlist, deleteitem } from "../../contexts/apicontexts";
import config from "../../config";

const PAGE_SIZE = 10;

export default function Organization() {
    const [organizations, setOrganizations] = useState([]);
    const [page, setPage] = useState(1);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoder, setIsLoder] = useState(false);
    const [errors, setErrors] = useState({});
    const [formState, setFormState] = useState({
        id: 0,
        name: "",
        organizationCode: "",
        address: "",
        mobile: "",
        registrationNumber: "",
        taxID: "",
        website: "",
        primaryEmail: "",
        description: "",
        enrollmentDate: new Date().toISOString().split('T')[0]
    });

    const validateForm = () => {
        const newErrors = {};

        if (!formState.name.trim()) newErrors.name = "Name is required";
        if (!formState.organizationCode.trim()) newErrors.organizationCode = "Code is required";
        if (!formState.mobile.trim()) newErrors.mobile = "Mobile is required";
        if (!formState.primaryEmail.trim()) {
            newErrors.primaryEmail = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formState.primaryEmail)) {
            newErrors.primaryEmail = "Email is invalid";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        setIsLoder(true);
        try {
            const response = await getlist(`${config.baseurl}Organizations/getlist`);
            if (response?.status === 200) {
                setOrganizations(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching organizations:", error);
        } finally {
            setIsLoder(false);
        }
    };

    const openAddModal = () => {
        setFormState({
            id: 0,
            name: "",
            organizationCode: "",
            address: "",
            mobile: "",
            registrationNumber: "",
            taxID: "",
            website: "",
            primaryEmail: "",
            description: "",
            enrollmentDate: new Date().toISOString().split('T')[0]
        });
        setErrors({});
        setIsOpen(true);
    };

    const openEditModal = (item) => {
        setFormState({
            ...item,
            enrollmentDate: item.enrollmentDate ? item.enrollmentDate.split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setErrors({});
        setIsOpen(true);
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsLoder(true);

        const payload = {
            ...formState,
            id: formState.id || 0
        };

        try {
            const url = payload.id > 0
                ? `${config.baseurl}Organizations/update`
                : `${config.baseurl}Organizations/add`;

            const response = await post(url, payload);

            if (response?.status === 200) {
                setIsOpen(false);
                setErrors({});
                fetchOrganizations();
            } else {
                console.error("Save failed:", response);
            }
        } catch (error) {
            console.error("API Error:", error);
        } finally {
            setIsLoder(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this organization?")) return;
        try {
            await deleteitem(`${config.baseurl}Organizations/delete`, id);
            fetchOrganizations();
        } catch (error) {
            console.error("Delete failed:", error);
        }
    };

    const paginatedData = organizations.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-[#0F111A] min-h-screen transition-colors duration-300">
            <Breadcrumb pageName="Organizations" currentPage="Organizations" />

            <div className="bg-white dark:bg-[#161B22] rounded-xl shadow border dark:border-gray-800 mt-4 overflow-hidden">
                {/* HEADER */}
                <div className="card-header flex justify-between items-center px-5 py-3 border-b transition-colors duration-300
                    bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]
                    dark:from-[#2D120D] dark:to-[#1A1A1A] dark:border-gray-800">

                    <div className="flex items-center gap-3">
                        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                            Organization List
                        </h2>
                        {isLoder && <DotLoader color="#ffffff" size="w-1.5 h-1.5" />}
                    </div>

                    <button
                        onClick={openAddModal}
                        className="flex items-center justify-center w-10 h-10 rounded-full transition-all
                        transform hover:scale-110 active:scale-95 shadow-lg
                        bg-white dark:bg-gray-200 text-[#C04C2A] dark:text-[#4A1B13]"
                    >
                        <FontAwesomeIcon icon={faPlus} className="text-lg" />
                    </button>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-800/50 uppercase text-[10px] tracking-wider text-gray-500 dark:text-gray-400">
                            <tr>
                                <th className="p-3 text-left">Name</th>
                                <th className="p-3 text-left">Code</th>
                                <th className="p-3 text-left">Email</th>
                                <th className="p-3 text-left">Mobile</th>
                                <th className="p-3 text-left">Address</th>
                                <th className="p-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map(item => (
                                    <tr key={item.id} className="bg-white dark:bg-[#161B22] border-t dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                        <td className="p-3 font-medium text-gray-800 dark:text-gray-200">{item.name}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400">{item.organizationCode}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400">{item.primaryEmail}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400">{item.mobile}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400 truncate max-w-xs">{item.address}</td>
                                        <td className="p-3 text-right space-x-3">
                                            <button
                                                onClick={() => openEditModal(item)}
                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                                title="Edit"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="text-red-600 hover:text-red-800 transition-colors"
                                                title="Delete"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-500 dark:text-gray-400 dark:bg-[#161B22]">
                                        No organizations found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {organizations.length > PAGE_SIZE && (
                    <div className="p-4 border-t dark:border-gray-800 dark:bg-gray-800/20 flex justify-end">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            Showing {paginatedData.length} of {organizations.length}
                        </span>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setIsOpen(false)}
                    ></div>

                    <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl p-8 relative shadow-2xl
                     transform transition-all animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">

                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                        >
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                        </button>

                        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
                            {formState.id ? "Update Organization" : "Create Organization"}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Name */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Organization Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                        ${errors.name ? "border-red-500 ring-red-100" : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                    value={formState.name}
                                    onChange={e => setFormState({ ...formState, name: e.target.value })}
                                />
                                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                            </div>

                            {/* Code */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Code <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                        ${errors.organizationCode ? "border-red-500 ring-red-100" : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                    value={formState.organizationCode}
                                    onChange={e => setFormState({ ...formState, organizationCode: e.target.value })}
                                />
                                {errors.organizationCode && <p className="mt-1 text-xs text-red-500">{errors.organizationCode}</p>}
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                        ${errors.primaryEmail ? "border-red-500 ring-red-100" : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                    value={formState.primaryEmail}
                                    onChange={e => setFormState({ ...formState, primaryEmail: e.target.value })}
                                />
                                {errors.primaryEmail && <p className="mt-1 text-xs text-red-500">{errors.primaryEmail}</p>}
                            </div>

                            {/* Mobile */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Mobile <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                        ${errors.mobile ? "border-red-500 ring-red-100" : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                    value={formState.mobile}
                                    onChange={e => setFormState({ ...formState, mobile: e.target.value })}
                                />
                                {errors.mobile && <p className="mt-1 text-xs text-red-500">{errors.mobile}</p>}
                            </div>

                            {/* Website */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Website
                                </label>
                                <input
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                    value={formState.website}
                                    onChange={e => setFormState({ ...formState, website: e.target.value })}
                                />
                            </div>

                            {/* Registration Number */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Registration No
                                </label>
                                <input
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                    value={formState.registrationNumber}
                                    onChange={e => setFormState({ ...formState, registrationNumber: e.target.value })}
                                />
                            </div>

                            {/* Tax ID */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Tax ID
                                </label>
                                <input
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                    value={formState.taxID}
                                    onChange={e => setFormState({ ...formState, taxID: e.target.value })}
                                />
                            </div>

                            {/* Enrollment Date */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Enrollment Date
                                </label>
                                <input
                                    type="date"
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                    value={formState.enrollmentDate}
                                    onChange={e => setFormState({ ...formState, enrollmentDate: e.target.value })}
                                />
                            </div>

                            {/* Address */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Address
                                </label>
                                <textarea
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A] h-20 resize-none"
                                    value={formState.address}
                                    onChange={e => setFormState({ ...formState, address: e.target.value })}
                                />
                            </div>

                            {/* Description */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A] h-20 resize-none"
                                    value={formState.description}
                                    onChange={e => setFormState({ ...formState, description: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-6">
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700
                                    text-gray-600 dark:text-gray-300 font-bold rounded-xl
                                    hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isLoder}
                                className="flex-1 px-4 py-3 text-white font-bold rounded-xl
                                    shadow-lg transform transition-all active:scale-95 disabled:opacity-70"
                                style={{
                                    background: "linear-gradient(to right, #C04C2A, #4A1B13)"
                                }}
                            >
                                {isLoder ? (
                                    <DotLoader color="#fff" size="w-1.5 h-1.5" />
                                ) : (
                                    formState.id ? "Update" : "Save"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
